import React from 'react';
import { Toolbar, CircularProgress, Grid, TextField, Card, CardHeader, CardContent, IconButton, withStyles } from '@material-ui/core';
import moment from 'moment';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Bar } from 'recharts';
import { withRouter } from 'react-router';
import { backendUrl } from '../App';
import logo from '../assets/img/LogoOficial.png';

const styles = theme => ({
    root: {
        flexGrow: 1,
        height: '100%',
        zIndex: 1,
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
    },
    drawerPaper: {
        position: 'relative',
        width: '300px',
    },
    content: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing.unit * 3,
        minWidth: 0, // So the Typography noWrap works
        overflowY: 'auto'
    },
    centered: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
});

const AdminHome = ({classes, ...props}) => {
    const currentDate = moment().format('YYYY-MM-DD');
    const [orders, setOrders] = React.useState([]);
    const [users, setUsers] = React.useState([]);
    const [chartsData, setChartsData] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [searching, setSearching] = React.useState(false);
    const [searchDate, setSearchDate] = React.useState(currentDate);

    const loadOrders = async (date, id) => {
        const token = sessionStorage.getItem('rtvcrespoToken');

        let url = null;
        if (date) {
            url = `${backendUrl}/orders/all?date=${date}`;
        }
        if (id) {
            url = `${backendUrl}/orders/all?id=${id}`;
        }
        const response = await fetch(url, {
            method: 'GET',
            headers: new Headers({
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            })
        });
        const orders = await response.json();
        return orders;
    }

    const compareStrings = (a, b) => {
        // Assuming you want case-insensitive comparison
        a = a.toLowerCase();
        b = b.toLowerCase();

        return (a < b) ? -1 : (a > b) ? 1 : 0
    }

    const loadUsers = async () => {
        const token = sessionStorage.getItem('rtvcrespoToken');
        const url =`${backendUrl}/users/all`;

        const response = await fetch(url, {
            method: 'GET',
            headers: new Headers({
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            })
        });
        const users = await response.json();

        users.sort((a, b) => {
            return compareStrings(a.name, b.name);
        });

        return users;
    }    

    const handleChangeFilterDate = async (event) => {
        setSearching(true);
        const newDate = event.target.value;
        const dbOrders = await loadOrders(newDate);
        const newChartsData = getChartsData(dbOrders, users);
        setSearchDate(newDate);
        setOrders(dbOrders);
        setChartsData(newChartsData);
        setSearching(false);
    }

    const getChartsData = (dataOrders, dataUsers) => {
        let chartsData = [];

        dataUsers.forEach(user => {
            user.orders = 0;
            user.total = 0;
            dataOrders.forEach(order => {
                if(order.user_id === user.id){
                    user.orders = user.orders + 1;
                    user.total = user.total + order.total_amount;
                }
            });
        });

        dataUsers.forEach(user => {
            let barChart = {
                name: user.name,
                orders: user.orders,
                total: user.total
            }
            chartsData.push(barChart);
        });

        return chartsData;
    }

    const init = async () => {
        setLoading(true);
        try {
            const dbOrders = await loadOrders(searchDate);
            const dbUsers = await loadUsers();
            const newChartsData = getChartsData(dbOrders, dbUsers);
            setOrders(dbOrders);
            setUsers(dbUsers);
            setChartsData(newChartsData);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setOrders([]);
            setUsers([]);
            setChartsData([]);
            setLoading(false);
        }
    };

    const logout = () => {
        sessionStorage.removeItem('rtvcrespoToken');
        props.history.push('/login');
    };

    React.useEffect(() => {
        init();
    }, []);

    return (
        <div className="full-height">
            <Toolbar className="toolbar-tpv">
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}
                >
                    <div className="img-external-border">
                        <div className="img-internal-border">
                            <img className='top-menu-cbx-logo' src={logo} alt="logo" />
                        </div>
                    </div>
                    
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center'
                        }}
                    >
                        <div>
                            <form noValidate>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <TextField
                                        id="date"
                                        label="Fecha"
                                        type="date"
                                        defaultValue={searchDate}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        onChange={handleChangeFilterDate}
                                    />
                                </div>
                            </form>
                        </div>

                        <div
                            style={{
                                marginLeft: '30px'
                            }}
                        >
                            <IconButton aria-label="Logout" onClick={logout}>
                                <i className="fas fa-sign-out-alt" style={{fontSize: '30px', color: 'white'}}></i>
                            </IconButton>
                        </div>
                    </div>
                </div>
            </Toolbar>

            <div className="container-with-top-menu">
                <div className={classes.root}>
                    {searching || loading ?
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <CircularProgress size={80} />
                        </div>
                        :
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                padding: 12
                            }}
                        >
                            <Grid container spacing={24} style={{height: '100%'}}>
                                <Grid item xs={12} sm={6} style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <Card style={{ width: '100%'}}>
                                        <CardHeader 
                                            title={"Nº de tickets"}
                                            classes={{title:classes.centered}}
                                        />
                                        <CardContent style={{ width: '100%', height: 260 }}>
                                            <ResponsiveContainer>
                                                <BarChart data={chartsData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="name" />
                                                    <YAxis allowDecimals={true}/>
                                                    <Tooltip />
                                                    <Legend />
                                                    <Bar dataKey="orders" fill="#FF6A6A" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <Card style={{ width: '100%'}}>
                                        <CardHeader 
                                            title={"Caja total en €"}
                                            classes={{title:classes.centered}}
                                        />
                                        <CardContent style={{ width: '100%', height: 260 }}>
                                            <ResponsiveContainer>
                                                <BarChart data={chartsData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="name" />
                                                    <YAxis allowDecimals={true}/>
                                                    <Tooltip />
                                                    <Legend />
                                                    <Bar dataKey="total" fill="#FF6A6A" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </div>
                    }
                </div>
            </div>
        </div>
    )
};

export default withRouter(withStyles(styles)(AdminHome));


// WEBPACK FOOTER //
// ./src/components/AdminHome.js